import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { UserInfo } from '@/services/types/user'
import { api } from '@/services/api/client'
import i18n from '@/i18n'

interface UserState {
  user: UserInfo | null
  token: string | null
  tokenExpiresAt: number | null
  isAuthenticated: boolean
  setUser: (user: UserInfo | null) => void
  setToken: (token: string | null) => void
  login: (user: UserInfo, token: string, expiresAt?: number) => void
  logout: () => void
}

export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      tokenExpiresAt: null,
      isAuthenticated: false,
      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      login: (user, token, expiresAt) => set({
        user,
        token,
        tokenExpiresAt: expiresAt ?? null,
        isAuthenticated: true,
      }),
      logout: () => {
        api.setAuthToken(null)
        set({
          user: null,
          token: null,
          tokenExpiresAt: null,
          isAuthenticated: false,
        })
      },
    }),
    {
      name: 'user-store',
      version: 2,
      migrate: (persisted: any, version: number) => {
        // v0→v1: add tokenExpiresAt field
        if (persisted.tokenExpiresAt === undefined) persisted.tokenExpiresAt = null
        // v1→v2: tokenExpiresAt seconds → milliseconds (backend uses UnixMilli)
        if (version < 2 && persisted.tokenExpiresAt && persisted.tokenExpiresAt < 1e12) {
          persisted.tokenExpiresAt *= 1000
        }
        return persisted
      },
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        tokenExpiresAt: state.tokenExpiresAt,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          // token 已过期（预留 60 秒缓冲）则清除登录状态
          if (state.tokenExpiresAt && Date.now() > state.tokenExpiresAt - 60_000) {
            state.logout()
            // 延迟到 React 挂载后再显示 Toast（rehydrate 在 React render 之前执行）
            setTimeout(() => {
              import('@/components/shared').then(({ showInfo }) => {
                showInfo(i18n.t('auth.sessionExpired'))
              }).catch(() => {})
            }, 500)
            return
          }
          api.setAuthToken(state.token)
        }
      },
    }
  )
)
